<script setup lang="ts">
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import useLanguage from '@/composables/useLanguage'
import { useReferencesStore } from '@/store/references'
import {
  IPillLinkItem,
  TReferenceClickOrigin,
  TReferenceTab,
  TTooltipAlignment,
  TTooltipDirection
} from '@lifk/types'
import { PhBookOpen } from '@phosphor-icons/vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    // For the click event
    reference: IPillLinkItem
    origin: TReferenceClickOrigin
    openToTab?: TReferenceTab
    // Visual customizations
    size?: number
    mobileSize?: number
    label?: string
    tooltipDirection?: TTooltipDirection
    tooltipAlignment?: TTooltipAlignment
  }>(),
  {
    size: 14,
    mobileSize: 24,
    tooltipDirection: 'top',
    tooltipAlignment: 'center'
  }
)

const i18n = useLanguage('UIReferenceIcon')
const referencesStore = useReferencesStore()

const isMobile = computed(() => useDetectBreakpoint('xs'))
const hovered = ref(false)

const onClicked = () => {
  referencesStore.openReference(
    props.reference.linkType,
    props.reference.key,
    props.origin,
    props.openToTab
  )
}
</script>

<template>
  <UIToolTip
    v-if="!isMobile"
    class="flex"
    :direction="tooltipDirection"
    :alignment="tooltipAlignment"
    :text="label ? label : i18n.learnMore"
    :reference="reference"
    variant="light"
    delay-tag="reference-card"
  >
    <button
      type="button"
      class="flex-shrink-0 border-none bg-transparent p-0 cursor-pointer"
      :aria-label="label ? label : i18n.ariaLearnMore"
      @mouseover="hovered = true"
      @mouseleave="hovered = false"
      @keydown.stop
      @click.stop.prevent="onClicked"
    >
      <PhBookOpen
        :size="isMobile ? mobileSize : size"
        class="fill-charcoal"
        :weight="hovered ? 'duotone' : 'regular'"
        aria-hidden="true"
      />
    </button>
  </UIToolTip>
  <button
    v-else
    type="button"
    class="flex-shrink-0 border-none bg-transparent p-0 cursor-pointer"
    :aria-label="label ? label : i18n.ariaLearnMore"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
    @click.stop.prevent="onClicked"
    @keydown.stop
  >
    <PhBookOpen
      :size="isMobile ? mobileSize : size"
      class="fill-charcoal"
      :weight="hovered ? 'duotone' : 'regular'"
      aria-hidden="true"
    />
  </button>
</template>
